import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "_redux/hooks";
import {
  selectAvailableSellersAndStore,
  selectSelectedMPSellerChoiceFromRedux,
  selectSelectedMPSellerIdFromLocalStorage,
} from "_redux/selectors/catalog";
import { SET_SELLER_CHOICE_ACTION } from "_redux/actions/catalog";
import { useSite } from "_foundationExt/hooks/useSite";
import { MARKETPLACE_SELLER_ID_SERVICEBUND } from "constants/common";

/**
 * This hook is used to manage the sellerChoice in the store.
 *
 * Itensures that the correct seller is set based on the data from
 * Redux and localStorage. It checks various conditions and updates the sellerChoice
 * accordingly to ensure consistency.
 */
export const useSellerChoice = () => {
  const dispatch = useAppDispatch();
  const { currentSite } = useSite();

  const sellers = useAppSelector(selectAvailableSellersAndStore);
  const selectedSellerIdFromLocalStorage = useAppSelector(
    selectSelectedMPSellerIdFromLocalStorage
  );
  const selectedSellerChoiceFromRedux = useAppSelector(
    selectSelectedMPSellerChoiceFromRedux
  );

  useEffect(() => {
    // Exit early if no current site exists or if it only has the SB seller
    if (!currentSite || sellers.length < 2) return;

    // Exit early if there is no valid site or the seller list is empty
    if (!currentSite || sellers.length < 1) return;

    let newSellerId: string | undefined;

    // Check localStorage first
    if (selectedSellerIdFromLocalStorage !== undefined) {
      newSellerId = sellers.some(
        (s) => s.extSellerId === selectedSellerIdFromLocalStorage
      )
        ? selectedSellerIdFromLocalStorage
        : sellers[0].extSellerId;
    }
    // If localStorage is undefined, check Redux state
    else if (selectedSellerChoiceFromRedux?.sellerId) {
      newSellerId = sellers.some(
        (s) => s.extSellerId === selectedSellerChoiceFromRedux.sellerId
      )
        ? selectedSellerChoiceFromRedux.sellerId
        : sellers[0].extSellerId;
    }

    // If neither localStorage nor Redux has a seller, force default to first seller
    if (!newSellerId) {
      newSellerId = sellers[0].extSellerId;
    }

    if (newSellerId !== selectedSellerChoiceFromRedux?.sellerId) {
      dispatch(
        SET_SELLER_CHOICE_ACTION(
          newSellerId ?? MARKETPLACE_SELLER_ID_SERVICEBUND
        )
      );
    }
  }, [
    currentSite,
    selectedSellerIdFromLocalStorage,
    selectedSellerChoiceFromRedux,
    sellers,
    dispatch,
  ]);
};
